import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AIChat = () => {
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const messagesEndRef = useRef(null);

    const userId = localStorage.getItem("email");

    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [feedbackQuestion, setFeedbackQuestion] = useState("");
    const [feedbackText, setFeedbackText] = useState("");

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Fetch existing chat sessions
        const fetchChats = async () => {
            try {
                const res = await axiosInstance.get(`/aichat/chat?user_id=${userId}`);
                console.log("Fetched chats:", res.data);
                setChats(res.data);
            } catch (error) {
                console.error("Error fetching chats:", error);
                toast.error("Error loading chat history.");
            }
        };

        fetchChats();
    }, [userId]);

    // Auto‐scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const submitFeedback = async () => {
        try {
            await axiosInstance.post("/aichat/feedback", {
                question: feedbackQuestion,
                feedback: feedbackText
            });
            toast.success("Thanks for your feedback");
            setIsFeedbackModalOpen(false);
            setFeedbackText("");
        } catch (err) {
            console.error(err);
            toast.error("Failed to send feedback.");
        }
    };

    const handleNewChat = () => {
        setSelectedChat(null);
        setMessages([]);
        setInputValue("");
    };

    const handleSendMessage = async () => {
        const text = inputValue.trim();
        if (!text) return;

        setIsLoading(true);

        const userMsg = { role: "user", content: text };
        setMessages((prev) => [...prev, userMsg]);
        setInputValue("");

        try {
            if (!selectedChat) {
                const res = await axiosInstance.post("/aichat/message", {
                    user_id: userId,
                    message: text,
                });
                const { ai_chat_id, thread_id, ai_message, description } = res.data;

                const newChat = { ai_chat_id, thread_id, description };

                setChats((prev) => [newChat, ...prev]);
                setSelectedChat(newChat);
                setMessages([userMsg, { role: "assistant", content: ai_message }]);
            } else {
                const res = await axiosInstance.post("/aichat/message", {
                    user_id: userId,
                    thread_id: selectedChat.thread_id,
                    message: text,
                });
                const content = res.data.ai_message || res.data.content;
                console.log("AI response:", content);
                setMessages((prev) => [...prev, { role: "assistant", content }]);
            }
        } catch (error) {
            console.error("Error sending message:", error);
            toast.error("Failed to send message.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectChat = async (chat) => {
        try {
            setSelectedChat(chat);
            setMessages([]);
            const res = await axiosInstance.get(
                `/aichat/messages?user_id=${userId}&thread_id=${chat.thread_id}`
            );
            setMessages(
                res.data
                    .map((m) => ({ role: m.role, content: m.content }))
                    .reverse() // oldest first
            );
        } catch (error) {
            console.error("Error loading messages:", error);
            toast.error("Error loading chat messages.");
        }
    };

    const handleDeleteChat = async (ai_chat_id) => {
        try {
            await axiosInstance.delete(`/aichat/chat/${ai_chat_id}`);
            toast.success("Chat deleted.");
            setChats((prev) => prev.filter((c) => c.ai_chat_id !== ai_chat_id));
            if (selectedChat?.ai_chat_id === ai_chat_id) {
                setSelectedChat(null);
                setMessages([]);
            }
        } catch (error) {
            console.error("Error deleting chat:", error);
            toast.error("Error deleting chat.");
        }
    };

    const openDeleteModal = (chat) => {
        setChatToDelete(chat);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (chatToDelete) {
            handleDeleteChat(chatToDelete.ai_chat_id);
        }
        setIsDeleteModalOpen(false);
        setChatToDelete(null);
    };

    const cancelDelete = () => {
        setIsDeleteModalOpen(false);
        setChatToDelete(null);
    };

    return (
        <div className="h-full flex flex-col bg-gray-50 p-4">
            {/* Page Title */}
            <h1 className="text-4xl font-extrabold mb-6 text-gray-800 border-b pb-4 text-center">AI Chat</h1>

            {/* Panels Container */}
            <div className="flex flex-1 overflow-hidden">
                {/* Left panel */}
                <aside className="flex-none w-1/5 pr-4 h-full">
                    <div className="h-full bg-white rounded-md flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b">
                            <button
                                onClick={() => window.location.href = "/feedback"}
                                className="w-full px-5 py-2 bg-teal-600 text-white rounded-lg font-semibold shadow-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-75 transition duration-200 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Feedback
                            </button>
                            <button
                                onClick={handleNewChat}
                                className="mt-6 w-full px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 ease-in-out transform hover:scale-105"
                            >
                                New Chat
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {chats.map((chat) => (
                                <div
                                    key={chat.ai_chat_id}
                                    onClick={() => handleSelectChat(chat)}
                                    tabIndex={0}  // make it focusable for the focus:ring styles
                                    className={`
                                        flex justify-between items-center
                                        px-5 py-2
                                        rounded-lg
                                        shadow-md
                                        font-semibold
                                        cursor-pointer
                                        transition duration-200 ease-in-out transform hover:scale-105
                                        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-75
                                        ${selectedChat?.ai_chat_id === chat.ai_chat_id
                                            ? "bg-indigo-600 text-white"
                                            : "bg-gray-200 text-gray-800 hover:bg-indigo-200"
                                        }
                                    `}
                                >
                                    {/* <span>Chat {chat.ai_chat_id}</span> */}
                                    <span class="truncate w-60">{chat.description}</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openDeleteModal(chat);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        ❌
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Right panel */}
                <main className="flex-1 pl-4 h-full">
                    <div className="h-full bg-white rounded-md shadow flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b">
                            <p className="text-gray-500">Start typing to chat with AI. Press <span className="font-semibold text-black">Shift + Enter</span> to add a new line.</p>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50 ">
                            {messages.length ? (
                                messages.map((msg, i) =>
                                    msg.role === "assistant" ? (
                                        <div key={i} className="mb-2">
                                            {/* assistant bubble */}
                                            <div className="flex justify-start">
                                                <div className="px-4 py-2 rounded-md max-w-[75%] bg-white border">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            p({ children, ...props }) {
                                                                return (
                                                                    <p className="mt-4 leading-normal" {...props}>
                                                                        {children}
                                                                    </p>
                                                                )
                                                            },
                                                            li({ children, ...props }) {
                                                                return (
                                                                    <li
                                                                        className="mt-4 prose-p:leading-normal"
                                                                        {...props}
                                                                    >
                                                                        {children}
                                                                    </li>
                                                                )
                                                            }
                                                        }}
                                                    >
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                            {/* feedback buttons */}
                                            <div className="flex space-x-4 ml-2 text-sm mt-2">
                                                <button
                                                    className="px-3 py-1 bg-green-100 text-green-800 font-medium rounded-lg hover:bg-green-200 transition-colors"
                                                    onClick={() => { toast.success("Thanks for your feedback"); }}
                                                >
                                                    Good
                                                </button>
                                                <button
                                                    className="px-3 py-1 bg-red-100 text-red-800 font-medium rounded-lg hover:bg-red-200 transition-colors"
                                                    onClick={() => {
                                                        const q =
                                                            i > 0 && messages[i - 1].role === "user"
                                                                ? messages[i - 1].content
                                                                : "";
                                                        setFeedbackQuestion(q);
                                                        setIsFeedbackModalOpen(true);
                                                    }}
                                                >
                                                    Bad
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // user bubble
                                        <div key={i} className="flex justify-end mb-2">
                                            <div className="px-4 py-2 rounded-md max-w-3/4 whitespace-pre-wrap bg-blue-600 text-white">
                                                {msg.content}
                                            </div>
                                        </div>
                                    )
                                )
                            ) : (
                                <div className="text-center text-gray-400">No messages yet.</div>
                            )}

                            {/* Loading indicator */}
                            {isLoading && (
                                <div className="flex justify-start mb-2">
                                    <div className="px-4 py-2 rounded-md max-w-3/4 bg-white border flex items-center space-x-2">
                                        <div className="w-5 h-5 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                        <span>AI is thinking...</span>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        <div className="px-6 py-4 border-t ">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                                className="flex flex-col space-y-3"
                            >
                                <textarea
                                    className="w-full border border-gray-300 p-3 rounded-lg resize-none focus:outline-none focus:ring"
                                    rows={3}
                                    placeholder="Type your message…"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    disabled={isLoading}
                                />

                                <div className="text-right">
                                    <button
                                        type="submit"
                                        className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-6 rounded-lg font-semibold disabled:opacity-50"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Sending…' : 'Send'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </main>
            </div>

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-100 p-6">
                        <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
                        <p className="mb-6">
                            Are you sure you want to delete this chat?
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={cancelDelete}
                                className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback Modal */}
            {isFeedbackModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-96 p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            We are sorry! How can we improve?
                        </h3>
                        <p className="mb-2">
                            <strong>Question:</strong> {feedbackQuestion}
                        </p>
                        <textarea
                            disabled={isLoading}
                            rows={4}
                            className="w-full border p-2 rounded mb-4"
                            placeholder="Enter your feedback..."
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                        />
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setIsFeedbackModalOpen(false)}
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={isLoading}
                                onClick={submitFeedback}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                {isLoading ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIChat;
